import { PaymentMethod } from '@app/models/PaymentMethod';
import { PaymentMethodApi } from '@app/services/apiServices/PaymentMethodApi';
import { SpinnerService } from '@app/services/spinner.service';
import { PropertiesService } from '@app/services/properties.service';
import { Property } from '@app/models/Property';
import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { KeyboardService } from '@app/services/Keyboard.service';
import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { UtilService } from "@app/services/Util.service";
import { take } from "rxjs/operators";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AppDataService } from "@app/services/appData.service";
import { DomSanitizer } from "@angular/platform-browser";
import { WindowToken } from "@app/util/window.token";
import { MatDialog } from '@angular/material/dialog';
import { PushPopService } from "@app/services/push.pop.service";
import {
    CardDetailsWithSchedulesDeleteDialog
} from "@app/pages/wallet/viewcarddetails/cardDetailsWithSchedulesDelete.dialog";
import { forkJoin } from "rxjs";
import { CardDetailsDeleteDialog } from "@app/pages/wallet/viewcarddetails/cardDetailsDelete.dialog";
import { ActivatedRoute, Router } from "@angular/router";

/**
 * Created by mac90082 on 10/21/16.
 * This page lobbies an embedded iFrame to achieve what it needs to. The iframe's source can be found PAY/digitalpayments.
 */
@Component({
    selector: 'app-view-card-details',
    template: require('./viewCardDetails.html')
})
export class ViewCardDetailsComponent implements OnInit, OnDestroy {
    paymentMethod: PaymentMethod;
    paymentMethodId: string;
    properties: Property[];
    // TODO change to correct object list once api is done
    paymentSchedules: any[];
    doneLoading: boolean;
    private navItems: any;

    constructor(@Inject(AppDataService) private appDataService: AppDataService,
                @Inject(DomSanitizer) private domSanitizer: DomSanitizer,
                @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(WindowToken) private $window: any,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(PropertiesService) private propertiesService: PropertiesService,
                @Inject(KeyboardService) private keyboardService: KeyboardService,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(PushPopService) private pushPopService: PushPopService,
                @Inject(UtilService) private UtilService: UtilService,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {}

    ngOnInit() {

        this.paymentMethodId = this.route.snapshot.params.paymentMethodId;
        this.spinnerService.showSpinner();
        this.doneLoading = false;
        this.navItems = this.pushPopService.getItems();
        this.get().then(() => {
            this.spinnerService.closeSpinner();
            this.doneLoading = true;
        }, () => {
            this.spinnerService.closeSpinner();
            this.doneLoading = true;
        });

    }

    ngOnDestroy() {
        // make sure soft keyboard is closed
        // (iframe on iOS and web results in any open soft keyboards remaining open otherwise)
        this.keyboardService.forceCloseSoftKeyboard();
    }

    /**
     * Gets card details for an id.
     * @returns {PaymentMethod} returns card payment method
     */
    get(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.paymentMethodApi.paymentMethodsIdGet(this.paymentMethodId)
                .pipe(take(1)).subscribe({
                    next: resp => {
                        this.paymentMethod = resp;
                        this.navItems = this.pushPopService.getItems();
                        this.paymentMethodApi.paymentMethodsIdPaymentSchedulesGet(this.paymentMethodId)
                            .pipe(take(1)).subscribe({
                                next: resp => {
                                    this.paymentSchedules = resp;
                                    if (this.paymentSchedules) {
                                        for (let i = 0; i < this.paymentSchedules.length; i++) {
                                            let nextFuturePayment =
                                        this.UtilService.findNextPayment(this.paymentSchedules[i].futurePayments);
                                            this.paymentSchedules[i].nextPaymentDate = nextFuturePayment.date;
                                            this.paymentSchedules[i].nextPaymentAmount = nextFuturePayment.total;
                                        }
                                    }
                                    resolve();
                                },
                                error: () => {
                                    reject();
                                }
                            });
                    }, error: err => {
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.get().then(() => {
                                    });
                                }
                                reject();
                            });
                    }
                });
        })
    }

    /**
     * Calls paymentMethodsIdDelete to delete a specific card from Payment Methods.
     */
    removeCard() {
        if (this.paymentSchedules && this.paymentSchedules.length > 0) {
            // Ask if user wants to delete related schedules, before deleting
            let scheduleConfirmationDialog = this.dialog.open(CardDetailsWithSchedulesDeleteDialog, {
                data: { paymentSchedules: this.paymentSchedules }
            });
            scheduleConfirmationDialog.afterClosed().pipe(take(1)).subscribe({
                next: data => {
                    if (data === 'Delete') {
                        this.spinnerService.showSpinner();
                        let observables = [];
                        for (let i = 0; i < this.paymentSchedules.length; i++) {
                            observables.push(
                                this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedules[i].id)
                                    .pipe(take(1))
                            );
                        }

                        forkJoin(observables).subscribe({
                            next: () => {
                                this.resetPropertyHistory();

                                // if successful, delete the payment method
                                this.paymentMethodApi.paymentMethodsIdDelete(this.paymentMethodId)
                                    .pipe(take(1)).subscribe({
                                        next: () => {
                                            this.spinnerService.closeSpinner();
                                            this.router.navigateByUrl('/wallet');
                                        },
                                        error: err => {
                                            this.networkErrorService.handleNetworkError(err, true)
                                                .pipe(take(1)).subscribe((resp) => {
                                                    if (resp === true) {
                                                        this.removeCard();
                                                    }
                                                });
                                        }
                                    })
                            }, error: err => {
                                let errMsg: string = undefined;
                                if (err.error && err.error.message) {
                                    errMsg = err.error.message;
                                }
                                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                                    .pipe(take(1)).subscribe({ next: () => {} });
                            }
                        })
                    }
                },
                error: err => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.removeCard();
                            }
                        });
                } })
        } else {    // no related schedules exist
            let dialogRef = this.dialog.open(CardDetailsDeleteDialog);
            dialogRef.afterClosed().pipe(take(1)).subscribe({ next: data => {
                if (data === "Delete") {
                    this.spinnerService.showSpinner();
                    this.paymentMethodApi.paymentMethodsIdDelete(this.paymentMethodId)
                        .pipe(take(1)).subscribe({ next: () => {
                            this.spinnerService.closeSpinner();
                            // payment method deleted - check if wallet is empty
                            this.getPaymentMethods(this.paymentMethod.payerId).then(() => {
                                this.resetPropertyHistory();
                                this.router.navigateByUrl('/wallet');
                            });
                        }, error: err => {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp === true) {
                                        this.removeCard();
                                    }
                                });
                        } });
                }
            }, error: err => {
                this.networkErrorService.handleNetworkError(err, true)
                    .pipe(take(1)).subscribe((resp) => {
                        if (resp === true) {
                            this.removeCard();
                        }
                    });
            } });
        }
    }

    resetPropertyHistory() {
        this.navItems = this.pushPopService.getItems();
        if (this.navItems && this.navItems[0].history && this.navItems[0].history.length > 1) {
            this.navItems[0].history = [];
            this.pushPopService.setItems(this.navItems);
        }
    }

    getPaymentMethods(payerId): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.paymentMethodApi.homeownersIdPaymentMethodsGet(payerId)
                .pipe(take(1)).subscribe({ next: (resp) => {
                    if (resp && resp.length > 0) {
                    // Payment methods exist
                        resolve();
                    } else {
                    // No payment methods in the wallet
                        resolve();
                    }
                }, error: (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe(() => {
                            reject();
                        });
                } });
        });
    }

    /**
     * Cancel and return to wallet landing page.
     */
    cancel() {
        this.router.navigateByUrl('/wallet');
    }
}

